import {
  Loader2, Search, ArrowLeft, Plus,
} from "lucide-react";
import {
  useCallback, useEffect, useRef, useState,
} from "react";
import BaseDrawer from "@/shared/components/Drawer";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import { Switch } from "@/shared/ui/Switch";
import { accountingService } from "../../services/accounting.service";
import { formatAmount } from "./utils";
import type { RefundEmployeeSearchResult, RefundSearchResult } from "../../types/accounting.types";

type RefundSearchDrawerProps = {
  open: boolean
  onClose: () => void
  isBusy: boolean
  onSelectOperation: (activityOperationId: number, operationData: {
    debit: number | null
    credit: number | null
    allocationBalance: number | null
    label: string
  }) => void
};

const RefundSearchDrawer = ({
  open,
  onClose,
  isBusy,
  onSelectOperation,
}: RefundSearchDrawerProps) => {
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employees, setEmployees] = useState<RefundEmployeeSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<RefundEmployeeSearchResult | null>(null);
  const [refunds, setRefunds] = useState<RefundSearchResult[]>([]);
  const [isLoadingRefunds, setIsLoadingRefunds] = useState(false);
  const [includeSettled, setIncludeSettled] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      setEmployeeSearch("");
      setEmployees([]);
      setSelectedEmployee(null);
      setRefunds([]);
      setIncludeSettled(false);
    }
  }, [open]);

  // Focus search input on open
  useEffect(() => {
    if (open && !selectedEmployee) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open, selectedEmployee]);

  // Debounced employee search
  const handleSearchChange = useCallback((value: string) => {
    setEmployeeSearch(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setEmployees([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await accountingService.searchRefundEmployees(value.trim());
        setEmployees(results);
      }
      catch {
        setEmployees([]);
      }
      finally {
        setIsSearching(false);
      }
    }, 300);
  }, []);

  // Load refunds for a given employee
  const loadRefunds = useCallback(async (employeeId: number, settled: boolean) => {
    setIsLoadingRefunds(true);
    try {
      const results = await accountingService.getRefundOperations(employeeId, settled);
      setRefunds(results);
    }
    catch {
      setRefunds([]);
    }
    finally {
      setIsLoadingRefunds(false);
    }
  }, []);

  // Load refunds when an employee is selected
  const handleSelectEmployee = useCallback(async (employee: RefundEmployeeSearchResult) => {
    setSelectedEmployee(employee);
    await loadRefunds(employee.id, includeSettled);
  }, [loadRefunds, includeSettled]);

  // Reload when switch changes
  const handleToggleIncludeSettled = useCallback((checked: boolean) => {
    setIncludeSettled(checked);
    if (selectedEmployee) {
      loadRefunds(selectedEmployee.id, checked);
    }
  }, [selectedEmployee, loadRefunds]);

  const handleBack = useCallback(() => {
    setSelectedEmployee(null);
    setRefunds([]);
    setIncludeSettled(false);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  }, []);

  return (
    <BaseDrawer
      drawerId="DRW-REFUND-SEARCH"
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      isFirstLevel={false}
      onBack={onClose}
      width="893px"
      zIndex={65}
      bodyClassName="[&_*]:select-text"
      title="💰 Affectation remboursement"
      description="Recherchez un employé puis sélectionnez l'opération à affecter."
    >
      {!selectedEmployee
        ? (
          // Step 1: Employee search
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                ref={searchInputRef}
                value={employeeSearch}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Rechercher un employé (nom, prénom, matricule)..."
                className="pl-9 h-9 text-[13px]"
              />
              {isSearching && (
                <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
              )}
            </div>

            {employees.length > 0 && (
              <div className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
                {employees.map((employee) => (
                  <button
                    key={employee.id}
                    type="button"
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors"
                    onClick={() => handleSelectEmployee(employee)}
                  >
                    <div className="text-[13px] font-medium text-slate-800">
                      {employee.firstName}
                      {" "}
                      {employee.lastName?.toUpperCase()}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {[employee.siteName, employee.staffNumber ? `Matricule: ${employee.staffNumber}` : null].filter(Boolean).join(" — ")}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {!isSearching && employeeSearch.trim().length >= 2 && employees.length === 0 && (
              <div className="text-center py-6 text-[12px] text-slate-500">
                Aucun employé trouvé avec des remboursements.
              </div>
            )}

            {!employeeSearch.trim() && (
              <div className="text-center py-8 text-[12px] text-slate-400">
                Saisissez un nom pour rechercher un employé ayant des remboursements.
              </div>
            )}
          </div>
        )
        : (
          // Step 2: Refunds & operations for selected employee
          <div className="space-y-3">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-700 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Retour à la recherche
            </button>

            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-[13px] font-medium text-slate-800">
                {selectedEmployee.firstName}
                {" "}
                {selectedEmployee.lastName?.toUpperCase()}
              </div>
              <div className="text-[11px] text-slate-500">
                {[selectedEmployee.siteName, selectedEmployee.staffNumber ? `Matricule: ${selectedEmployee.staffNumber}` : null].filter(Boolean).join(" — ")}
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <Switch
                checked={includeSettled}
                onCheckedChange={handleToggleIncludeSettled}
              />
              <span className="text-[12px] text-slate-600">Inclure les opérations soldées</span>
            </label>

            {isLoadingRefunds && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            )}

            {!isLoadingRefunds && refunds.length === 0 && (
              <div className="text-center py-6 text-[12px] text-slate-500">
                {includeSettled
                  ? "Aucun remboursement trouvé pour cet employé."
                  : "Aucun remboursement avec des opérations non soldées."}
              </div>
            )}

            {!isLoadingRefunds && refunds.map((refund) => (
              <div key={refund.id} className="rounded-md border border-amber-200 bg-amber-50/60 p-3 space-y-2">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-[12px] font-semibold text-amber-900">
                    R
                    {refund.id}
                  </div>
                  {refund.statusName && (
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      refund.paymentBlocked
                        ? "bg-red-100 text-red-800"
                        : "bg-amber-200/70 text-amber-900"
                    }`}
                    >
                      {refund.statusName}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-amber-700">
                  Montant retenu :
                  {" "}
                  {formatAmount(refund.amountRetained ?? 0)}
                  {" "}
                  € — Solde :
                  {" "}
                  {formatAmount(refund.allocationBalance ?? 0)}
                  {" "}
                  €
                </div>

                {refund.paymentBlocked && (
                  <div className="rounded border border-red-200 bg-red-50 px-2.5 py-2 text-[11px] text-red-800">
                    Aucun paiement ne peut être affecté à une demande «&nbsp;
                    {refund.statusName}
                    &nbsp;». Reprendre le statut de la demande si le paiement est voulu.
                  </div>
                )}

                <div className="space-y-1">
                  {refund.operations.map((op) => (
                    <div
                      key={op.id}
                      className="flex items-center gap-2 rounded border border-amber-100 bg-white px-2.5 py-2"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] text-slate-700 truncate" title={op.label}>
                          {op.label || `Opération #${op.id}`}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          {op.debit ? `Débit: ${formatAmount(op.debit)} €` : ""}
                          {op.credit ? `Crédit: ${formatAmount(op.credit)} €` : ""}
                          {" — "}
                          Solde à affecter :
                          {formatAmount(op.allocationBalance ?? 0)}
                          {" "}
                          €
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="success"
                        size="sm"
                        className="h-7 px-2.5 text-[11px] shrink-0"
                        disabled={isBusy || refund.paymentBlocked}
                        title={refund.paymentBlocked
                          ? `Demande « ${refund.statusName} » : aucun paiement ne peut lui être affecté.`
                          : undefined}
                        onClick={() => onSelectOperation(op.id, {
                          debit: op.debit,
                          credit: op.credit,
                          allocationBalance: op.allocationBalance,
                          label: op.label,
                        })}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Affecter
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
    </BaseDrawer>
  );
};

export default RefundSearchDrawer;
